import { Container, Sprite } from 'pixi.js';

import { config, getItemActivityData, getObjectInfo, isInitialItem } from '../config/config';
import Item from './Item';
import ActiveObject from './ActiveObject';
import { clamp } from '../helpers/tools';
import ExtraObject from './ExtraObject';
import { extraConfig } from '../config/extraConfig';
import { AreaWithId, ItemPosition, ObjectPosition, PositionsConfig } from '../config/positionsConfig';
import { Coordinates } from './declarations/PositionInfo';

export default class Layer extends Container {
  public override name: string;
  public size: Coordinates;
  public extra: { [key: string]: ExtraObject };
  public items: Item[];
  public objects: ActiveObject[];

  constructor(layerIndex: number, layerData: PositionsConfig, size: Coordinates) {
    super();

    this.name = `Layer_${layerIndex}_${layerData.name}`;
    this.size = size;
    this.extra = {};
    this.items = [];
    this.objects = [];
    this.sortableChildren = true;

    //console.log(`creating layer ${this.name}`, layerData, size);

    this.createBg(layerData.bg);
    this.createExtra(layerData.extra);
    this.createItems(layerData.items);
    this.createObjects(layerData.objects);
  }

  createBg(bgData: AreaWithId[]) {
    if (bgData && bgData.length > 0) {
      bgData.forEach((data) => {
        const bg = Sprite.from(data.id);
        bg.zIndex = 1;
        bg.eventMode = 'passive'; // todo ??? = "none"
        bg.x = data.x;
        bg.y = data.y;
        this.addChild(bg);
      });
    }
  }

  createItems(itemsData: ItemPosition[]) {
    if (itemsData && itemsData.length > 0) {
      itemsData.forEach((positionInfo) => {
        const activity = getItemActivityData(positionInfo.id);

        if (activity) {
          const item = new Item(positionInfo, activity);

          if (isInitialItem(positionInfo.id)) {
            item.activeItem.show(true, true);
          }

          this.items.push(item);
          this.addChild(item.activeItem);
        } else {
          console.error(`Activity data not found ${positionInfo.id}`);
        }
      });
    }
  }

  createObjects(objectsData: ObjectPosition[]) {
    if (objectsData && objectsData.length > 0) {
      objectsData.forEach((positionInfo) => {
        const objectInfo = getObjectInfo(positionInfo.id);
        const object = new ActiveObject(
          positionInfo.id,
          { x: positionInfo.x, y: positionInfo.y },
          positionInfo.hit,
          objectInfo,
        );
        this.objects.push(object);
        this.addChild(object);
      });
    }
  }

  createExtra(extraData: AreaWithId[]) {
    if (extraData && extraData.length > 0) {
      extraData.forEach((positionInfo) => {
        const numFrames =
          extraConfig[positionInfo.id] && extraConfig[positionInfo.id].numFrames
            ? extraConfig[positionInfo.id].numFrames
            : 1;

        const hitArea = {
          x: positionInfo.x,
          y: positionInfo.y,
          w: positionInfo.w,
          h: positionInfo.h,
        };
        const extra = new ExtraObject(positionInfo.id, { x: positionInfo.x, y: positionInfo.y }, numFrames, hitArea);
        this.extra[positionInfo.id] = extra;
        this.addChild(extra);
      });
    }
  }

  //ratio -0.5 - 0.5
  setPosition(ratio: Coordinates) {
    const pos = this.ratioToPosition(ratio);
    this.x = pos.x;
    this.y = pos.y;
  }

  ratioToPosition(ratio: Coordinates) {
    return {
      x: -(this.size.x - config.SIZES.MAX_WIDTH) * ratio.x,
      y: -(this.size.y - config.SIZES.MAX_HEIGHT) * ratio.y,
    };
  }

  positionToRatio(position: Coordinates) {
    return {
      x:
        this.size.x - config.SIZES.MAX_WIDTH === 0
          ? 0
          : clamp(-position.x / (this.size.x - config.SIZES.MAX_WIDTH), -0.5, 0.5),
      y:
        this.size.y - config.SIZES.MAX_HEIGHT === 0
          ? 0
          : clamp(-position.y / (this.size.y - config.SIZES.MAX_HEIGHT), -0.5, 0.5),
    };
  }

  //layerPoint (0-config.SIZES.SCENE_WIDTH, 0 - config.SIZES.SCENE_HEIGHT)
  public getStageRatio(layerPoint: Coordinates) {
    const fromCenterPosition = {
      x: config.SIZES.SCENE_WIDTH / 2 - layerPoint.x,
      y: config.SIZES.SCENE_HEIGHT / 2 - layerPoint.y,
    };
    return this.positionToRatio(fromCenterPosition);
  }
}
